import {
  DepthStencilFormat,
  DepthTexture,
  Mesh,
  OrthographicCamera,
  PlaneGeometry,
  Scene,
  ShaderMaterial,
  UnsignedInt248Type,
  WebGLRenderer,
  WebGLRenderTarget,
} from 'three';

import edlFrag from '../../../../../static/glsl/cesium/edl-pass.frag';
import edlVert from '../../../../../static/glsl/cesium/edl-pass.vert';

/**
 * Eye Dome Lighting post-process pass.
 *
 * Usage:
 *   edlPass.render(renderer, () => { /* normal scene render *\/ });
 *
 * Internally renders the scene to an offscreen target (capturing depth),
 * then composites it to the screen with depth-edge shading applied.
 */
export class EdlPass {
  /** The active EdlPass instance, set when the point cloud scene activates. */
  static current: EdlPass | undefined;

  /** Toggle EDL on/off at runtime. window.__pc.edlEnabled */
  enabled = false;

  private renderTarget: WebGLRenderTarget;
  private material: ShaderMaterial;
  private quadGeometry: PlaneGeometry;
  private quadScene: Scene;
  private quadCamera: OrthographicCamera;

  constructor(width: number, height: number) {
    EdlPass.current = this;
    // Depth with stencil: the scene drawn into this target can use the stencil too, such as a
    // translucent orthophoto, which draws each pixel from one tile only
    const depthTexture = new DepthTexture(width, height, UnsignedInt248Type);
    depthTexture.format = DepthStencilFormat;

    this.renderTarget = new WebGLRenderTarget(width, height, {
      depthTexture,
      depthBuffer: true,
      stencilBuffer: true,
    });

    this.material = new ShaderMaterial({
      vertexShader: edlVert,
      fragmentShader: edlFrag,
      uniforms: {
        tDiffuse: { value: this.renderTarget.texture },
        tDepth: { value: depthTexture },
        resolution: { value: [width, height] },
        edlStrength: { value: 1.0 },
        edlRadius: { value: 1.5 },
        cameraNear: { value: 0.2 },
        cameraFar: { value: 20000 },
      },
      depthWrite: false,
      depthTest: false,
    });

    // Fullscreen quad: PlaneGeometry covers NDC [-1, 1] in both axes.
    // OrthographicCamera maps the same range so the quad fills the screen.
    this.quadGeometry = new PlaneGeometry(2, 2);
    const quad = new Mesh(this.quadGeometry, this.material);
    quad.frustumCulled = false;
    this.quadScene = new Scene();
    this.quadScene.add(quad);
    this.quadCamera = new OrthographicCamera(-1, 1, 1, -1, 0, 1);
  }

  setSize(width: number, height: number) {
    this.renderTarget.setSize(width, height);
    const u = this.material.uniforms;
    u.resolution.value = [width, height];
    // Re-point the texture references after resize (setSize re-creates textures).
    u.tDiffuse.value = this.renderTarget.texture;
    u.tDepth.value = this.renderTarget.depthTexture;
  }

  /** Update near/far so the depth linearisation matches the active camera. */
  setCameraPlanes(near: number, far: number) {
    this.material.uniforms.cameraNear.value = near;
    this.material.uniforms.cameraFar.value = far;
  }

  get edlStrength(): number {
    return this.material.uniforms.edlStrength.value;
  }
  set edlStrength(v: number) {
    this.material.uniforms.edlStrength.value = v;
  }

  get edlRadius(): number {
    return this.material.uniforms.edlRadius.value;
  }
  set edlRadius(v: number) {
    this.material.uniforms.edlRadius.value = v;
  }

  /**
   * Renders the scene with EDL.
   * @param renderer  The active WebGLRenderer.
   * @param drawScene Callback that performs the normal scene render
   *                  (e.g. SunManager.renderObjectWithLightSources).
   */
  render(renderer: WebGLRenderer, drawScene: () => void) {
    if (!this.enabled) {
      drawScene();
      return;
    }

    const savedTarget = renderer.getRenderTarget();

    // 1. Render scene into offscreen target (captures colour + depth).
    renderer.setRenderTarget(this.renderTarget);
    renderer.clear();
    drawScene();

    // 2. Composite EDL-shaded result to the saved target (usually screen).
    renderer.setRenderTarget(savedTarget);
    renderer.render(this.quadScene, this.quadCamera);
  }

  dispose() {
    if (EdlPass.current === this) {
      EdlPass.current = undefined;
    }
    this.renderTarget.dispose();
    this.material.dispose();
    this.quadGeometry.dispose();
  }
}
